import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  MapPin,
  TrendingUp,
  ArrowRight,
  Calendar,
  Lock,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "../App";

const PROPERTY_TYPE_LABELS = {
  single_family: "Single Family",
  multi_family: "Multi-Family",
  apartment: "Apartment",
  commercial: "Commercial",
  land: "Land",
  mixed_use: "Mixed Use",
};

const DEAL_TYPE_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const DEAL_TYPE_COLORS = {
  flip: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  rental: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  development: "bg-purple-500/10 text-purple-500 border-purple-500/20",
};

const DealCard = ({ deal, viewMode = "grid", onViewDeal }) => {
  const { user } = useAuth();
  const profit =
    deal.estimated_arv - deal.asking_price - deal.estimated_rehab_cost;
  const roi = ((profit / deal.asking_price) * 100).toFixed(1);

  const isReserved =
    deal.reserved_by !== null && deal.reserved_by !== undefined;
  const isReservedByMe = deal.reserved_by === user?.user_id;
  const isReservedByOther = isReserved && !isReservedByMe;

  const formatCurrency = (value) => "$" + value.toLocaleString();

  const getPhotoUrl = () => {
    if (deal.photos?.front_exterior?.url) return deal.photos.front_exterior.url;
    const colors = { flip: "F97316", rental: "3B82F6", development: "8B5CF6" };
    const color = colors[deal.deal_type] || "64748B";
    return `https://placehold.co/400x300/${color}/FFFFFF?text=${encodeURIComponent(deal.city || "Property")}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (viewMode === "list") {
    return (
      <Card
        className="overflow-hidden hover:shadow-lg transition-shadow"
        data-testid={`deal-card-${deal.deal_id}`}
      >
        <CardContent className="p-0">
          <div className="flex items-stretch">
            <div className="w-48 h-36 flex-shrink-0">
              <img
                src={getPhotoUrl()}
                alt={deal.property_address}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 p-4 flex items-center justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={DEAL_TYPE_COLORS[deal.deal_type]}
                  >
                    {DEAL_TYPE_LABELS[deal.deal_type] || deal.deal_type}
                  </Badge>
                  <Badge variant="outline" className="bg-muted">
                    {PROPERTY_TYPE_LABELS[deal.property_type] ||
                      deal.property_type}
                  </Badge>
                </div>
                <h3 className="font-heading font-semibold text-foreground">
                  {deal.property_address}
                </h3>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {deal.city}, {deal.state}
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Purchase</p>
                  <p className="font-semibold">
                    {formatCurrency(deal.asking_price)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">ARV</p>
                  <p className="font-semibold">
                    {formatCurrency(deal.estimated_arv)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Rehab</p>
                  <p className="font-semibold">
                    {formatCurrency(deal.estimated_rehab_cost)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Est. Profit</p>
                  <p className="font-semibold text-green-500">
                    {formatCurrency(profit)}
                  </p>
                </div>
                <Button
                  onClick={onViewDeal}
                  data-testid={`view-deal-${deal.deal_id}`}
                >
                  View Deal <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${isReservedByOther ? "opacity-75" : ""}`}
      data-testid={`deal-card-${deal.deal_id}`}
    >
      <div className="relative h-32 overflow-hidden">
        <img
          src={getPhotoUrl()}
          alt={deal.property_address}
          className="w-full h-full object-cover"
        />

        {/* Status badge — top-left, always first */}
        {isReservedByMe && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-blue-500 text-white border-0 text-xs px-1.5 py-0">
              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
              Reserved by You
            </Badge>
          </div>
        )}
        {isReservedByOther && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-orange-500 text-white border-0 text-xs px-1.5 py-0">
              <Lock className="w-2.5 h-2.5 mr-0.5" />
              Under Contract
            </Badge>
          </div>
        )}
        {deal.status === "in_closing" && (
          <div className="absolute top-2 left-2">
            <Badge className="bg-purple-500 text-white border-0 text-xs px-1.5 py-0">
              In Closing
            </Badge>
          </div>
        )}

        {/* Deal type badge — below status badge */}
        <div className="absolute top-8 left-2">
          <Badge
            className={`${DEAL_TYPE_COLORS[deal.deal_type]} border text-xs px-1.5 py-0`}
          >
            {DEAL_TYPE_LABELS[deal.deal_type] || deal.deal_type}
          </Badge>
        </div>

        {/* Property type badge — top-right */}
        <div className="absolute top-2 right-2">
          <Badge
            variant="secondary"
            className="bg-background/90 text-foreground text-xs px-1.5 py-0"
          >
            {PROPERTY_TYPE_LABELS[deal.property_type] || deal.property_type}
          </Badge>
        </div>

        {/* ROI — bottom-right */}
        <div className="absolute bottom-2 right-2">
          <div className="bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
            <TrendingUp className="w-2.5 h-2.5" />
            {roi}% ROI
          </div>
        </div>
      </div>

      <CardContent className="p-3 space-y-3">
        <div>
          <div className="flex items-center gap-1 text-muted-foreground text-xs mb-0.5">
            <MapPin className="w-3 h-3" />
            {deal.city}, {deal.state}
          </div>
          <h3 className="font-heading font-semibold text-foreground text-sm line-clamp-1">
            {deal.property_address}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-xs text-muted-foreground mb-0.5">
              Purchase Price
            </p>
            <p className="font-semibold text-foreground text-sm">
              {formatCurrency(deal.asking_price)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-xs text-muted-foreground mb-0.5">ARV</p>
            <p className="font-semibold text-foreground text-sm">
              {formatCurrency(deal.estimated_arv)}
            </p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <p className="text-xs text-muted-foreground mb-0.5">Est. Rehab</p>
            <p className="font-semibold text-foreground text-sm">
              {formatCurrency(deal.estimated_rehab_cost)}
            </p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-2">
            <p className="text-xs text-green-600 dark:text-green-400 mb-0.5">
              Est. Profit
            </p>
            <p className="font-semibold text-green-600 dark:text-green-400 text-sm">
              {formatCurrency(profit)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="w-3 h-3" />
            {formatDate(deal.created_at)}
          </div>
          <Button
            size="sm"
            onClick={onViewDeal}
            data-testid={`view-deal-${deal.deal_id}`}
          >
            View Deal <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DealCard;
